// i18n dictionary. Keys are stable identifiers; English is the source/fallback.
// Any key missing from a language falls back to English (see LanguageContext.t),
// so screens can be translated incrementally without breaking untranslated text.
export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
];

export const STRINGS = {
  en: {
    // Auth
    'login.subtitle': 'Production Documentation System',
    'login.username': 'Username',
    'login.username.ph': 'Enter your username',
    'login.password': 'Password',
    'login.password.ph': 'Enter your password',
    'login.signin': 'Sign In',
    'login.signingin': 'Signing in...',
    'login.forgot': 'Forgot password?',
    'login.forgot.msg': 'Contact your administrator to reset your password.',
    'login.enterboth': 'Enter username and password',
    'login.install': 'Install the tablet app',
    'login.secure': 'Secure · HTTPS enforced · Role-based access',
    'login.offline.hint': 'No internet connection. You can sign in if you have signed in on this tablet before.',
    'login.offline.unknown': 'No internet connection, and this account has never signed in on this tablet. Connect to the internet once to sign in.',
    'login.offline.wrong': 'Wrong password. Wait {seconds} s before trying again.',
    'login.offline.wait': 'Too many wrong attempts. Try again in {seconds} s.',
    'login.offline.unsupported': 'No internet connection. Signing in without internet is not available on this device.',
    'login.offline.expired': 'It has been more than 14 days since you signed in on this tablet with an internet connection. Connect to wifi and sign in once.',
    'login.session.rejected': 'The server ended your session (password changed or account deactivated). Sign in again with an internet connection. Photos and orders in the queue are kept.',
    // Change password
    'pw.set.title': 'Set Your Password',
    'pw.change.title': 'Change Password',
    'pw.forced.note': 'You must set a new password before continuing. This step cannot be skipped.',
    'pw.current': 'Current Password',
    'pw.current.ph': 'Enter current password',
    'pw.new': 'New Password',
    'pw.new.ph': 'Enter new password',
    'pw.confirm': 'Confirm New Password',
    'pw.confirm.ph': 'Confirm new password',
    'pw.requirements': 'Password Requirements',
    'pw.req.length': 'At least 12 characters',
    'pw.req.upper': 'Uppercase letter',
    'pw.req.lower': 'Lowercase letter',
    'pw.req.number': 'Number',
    'pw.req.special': 'Special character',
    'pw.show': 'Show passwords',
    'pw.submit': 'Set New Password',
    'pw.updating': 'Updating...',
    'pw.updated': 'Password updated',
    'pw.nomatch': 'Passwords do not match',
    'pw.notmet': 'Password does not meet requirements',
    'pw.current.required': 'Current password required',
    // Home
    'home.production': 'Production',
    'home.newsession': 'Start New Session',
    'home.myorders': 'My Orders',
    'home.newsession.short': 'New Session',
    'home.startphotographing': 'Start photographing',
    'home.searchorders': 'Search Orders',
    'home.findbyorder': 'Find by order number',
    'home.reviewqueue': 'Review queue',
    'home.reviewqueue.desc': 'To discuss, feedback, pin comments',
    'home.feedback.title': 'Feedback',
    'home.feedback.toreshoot': 'photos to re-shoot',
    // Bottom nav
    'nav.home': 'Home',
    'nav.new': 'New',
    'nav.myorders': 'My Orders',
    'nav.feedback': 'Feedback',
    'nav.review': 'Review',
    'nav.search': 'Search',
    'nav.settings': 'Settings',
    // New order
    'order.new': 'New Order',
    'order.step1': 'Step 1 — Order Number',
    'order.scan': 'Scan or type order number',
    'order.novalidation': 'Any format accepted — no validation',
    'order.confirmnumber': 'Confirm order number:',
    'order.confirm': 'Confirm Order Number',
    'order.edit': 'Edit',
    'order.confirmed': 'Confirmed',
    'order.step2': 'Step 2 — Product Name',
    'order.productph': 'e.g. Leather Jacket Model A',
    'order.autocomplete': 'Autocomplete from previous entries',
    'order.model.select': 'Select a model…',
    'order.model.search': 'Type the first letters of the model…',
    'order.model.nomatch': 'No model found. Check the spelling, or tick "Undocumented model" below to type the name.',
    'order.model.selected': 'Model selected. Edit the text to search again.',
    'order.model.choose': 'Choose from the model list, or tick below for a model not yet in the list.',
    'order.undocumented': 'Undocumented model (not in the list)',
    'order.model.nameph': 'Type the model name',
    'order.model.namerequired': 'Enter the model name',
    'order.nomodels': 'No models in the list yet — tick "Undocumented model" to type one.',
    'order.start': 'Start Photo Session',
    'order.starting': 'Starting session...',
    'order.productrequired': 'Product name required',
    'order.offline.saved': 'Offline — session saved, will sync when back online',
    // Guided photo
    'guided.step': 'Step',
    'guided.of': 'of',
    'guided.captured': 'captured',
    'guided.restart': 'Restart',
    'guided.extra': 'Extra',
    'guided.taptotake': 'Tap to take photo',
    'guided.orientation': 'Portrait or landscape',
    'guided.retake': 'Retake',
    'guided.na': 'N/A',
    'guided.namarked': 'Marked as N/A',
    'guided.tapinstead': 'Tap to take photo instead',
    'guided.prev': 'Prev',
    'guided.next': 'Next',
    'guided.capture': 'Capture',
    'guided.review': 'Review',
    'guided.restart.title': 'Restart Session?',
    'guided.restart.msg': "All captured photos in this session will be discarded and you'll start from Step 1.",
    'guided.restart.yes': 'Yes, Restart',
    'guided.restarted': 'Session restarted',
    // Session summary
    'summary.title': 'Review Session',
    'summary.photos': 'Photos',
    'summary.na': 'N/A',
    'summary.missing': 'Missing',
    'summary.captured': 'Captured',
    'summary.notcaptured': 'Not captured',
    'summary.namarked': 'Marked N/A',
    'summary.take': 'Take',
    'summary.retake': 'Retake',
    'summary.back': 'Back',
    'summary.submit': 'Submit & Save',
    'summary.saving': 'Saving',
    'summary.offline.saved': 'Saved offline — photos will sync when back online',
    'summary.background': 'Saved — photos are uploading in the background',
    // Feedback
    'feedback.title': 'Feedback',
    'feedback.none': 'No photos to re-shoot',
    'feedback.toreshoot': 'to re-shoot',
    'feedback.allclear': 'All clear',
    'feedback.allclear.msg': 'None of your photos were rejected. Nice work!',
    'feedback.reshoot': 'Re-shoot',
    'feedback.rejected': 'Rejected',
    'feedback.nonote': 'no note left',
    'feedback.done': 'Done',
    'feedback.markeddone': 'Marked as done',
    // Common
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.signout': 'Sign out',
    'common.settings': 'Settings',
    // Complaint module (C6). English only for now; PL falls back to these.
    'home.complaints': 'Reports',
    'home.complaints.desc': 'Complaint reports per factory, open complaints',
    'nav.complaints': 'Reports',
    'complaints.title': 'Complaints',
    'complaints.new': 'New complaint',
    'complaints.reports': 'Reports',
    'complaints.back': 'Back',
    'complaints.readonly.note': 'Read-only: you see the complaints for your own factory.',
    'complaints.search.ph': 'Order number, customer or retailer reference...',
    'complaints.period': 'Period',
    'complaints.from': 'From',
    'complaints.to': 'to',
    'complaints.all': 'All',
    'complaints.allfactories': 'All factories',
    'complaints.reset': 'Reset',
    'complaints.reset.title': 'Back to the last 30 days, no filters',
    'complaints.status': 'Status',
    'complaints.status.any': 'Any status',
    'complaints.status.open': 'Open',
    'complaints.status.incomplete_complaint': 'Incomplete complaint',
    'complaints.status.pending_verdict': 'Pending verdict',
    'complaints.status.incomplete_verdict': 'Incomplete verdict',
    'complaints.status.solved': 'Solved',
    'complaints.status.cancelled': 'Cancelled',
    'complaints.empty.title': 'No complaints found',
    'complaints.empty.period': 'Nothing registered in this period. Switch to "All" or widen the dates.',
    'complaints.empty.all': 'No complaints match these filters.',
    'complaints.count.one': 'complaint',
    'complaints.count.many': 'complaints',
    'complaints.summary': '{solved} of {total} solved',
    'complaints.noenrichment': 'No customer or model known yet',
    'complaints.productiondate': 'Production date',
    'complaints.deliverydate': 'Delivery date',
    'complaints.fallback': 'Delivery fallback',
    'complaints.fallback.title': 'No production photo found for this order, so the Blueware delivery date is used as the reporting date.',
    'complaints.noproductiondate': 'No production date',
    'complaints.registered': 'Registered',
    // New complaint
    'complaints.ordernumber': 'Order number',
    'complaints.lookup': 'Look up',
    'complaints.lookingup': 'Looking up...',
    'complaints.lookup.hint': 'Checks what PhotoQA and Blueware know about this order before you register it.',
    'complaints.exists': 'This order already has a complaint dossier',
    'complaints.exists.msg': 'Add the new issue as a complaint line in the existing dossier, so the order keeps one history.',
    'complaints.exists.open': 'Open existing dossier',
    'complaints.notfound': 'Not found in PhotoQA or Blueware. Check the order number; you can still register it.',
    'complaints.notfound.short': 'Not found',
    'complaints.found': 'Found',
    'complaints.known': 'What we know about this order',
    'complaints.model': 'Model',
    'complaints.factory': 'Factory',
    'complaints.customer': 'Customer',
    'complaints.variant': 'Customer variant',
    'complaints.fabric': 'Fabric',
    'complaints.delivered': 'Delivered',
    'complaints.commission': 'Commission no.',
    'complaints.refs': 'References',
    'complaints.retailerref': 'Retailer reference',
    'complaints.consumer': 'Consumer name',
    'complaints.partner': 'Service partner',
    'complaints.caseref': 'Service case number',
    'complaints.notes': 'Notes',
    'complaints.create': 'Register complaint',
    'complaints.creating': 'Registering...',
    'complaints.create.hint': 'Customer, model, fabric and production date are stored with the complaint now, so reports never depend on a live lookup.',
    'complaints.created': 'Complaint registered',
    // Dossier
    'complaints.dossier': 'Dossier',
    'complaints.loadfailed': 'Could not load this complaint.',
    'complaints.taxonomy.failed': 'Could not load the choice lists. Reload the page to edit complaint lines.',
    'complaints.openorder': 'Open order in PhotoQA',
    'complaints.edit': 'Edit',
    'complaints.refs.saved': 'References saved',
    'complaints.lines': 'Complaint lines',
    'complaints.line': 'Line',
    'complaints.addline': 'Add complaint line',
    'complaints.newline': 'New complaint line',
    'complaints.nolines': 'No complaint lines yet. Each issue gets its own line with its own status.',
    'complaints.nodetail': 'Complaint not specified yet',
    'complaints.continue': 'Continue',
    'complaints.close': 'Close',
    'complaints.leadtime': 'Solved in {days} days',
    'complaints.closedtimes': 'closed {n} times',
    'complaints.attach': 'Attach',
    'complaints.reopen': 'Reopen',
    'complaints.uncancel': 'Undo cancel',
    'complaints.line.uncancelled': 'Cancel undone',
    'complaints.event.uncancelled': 'Cancel undone',
    'complaints.cancelline': 'Cancel line',
    'complaints.cancel.reason': 'Reason for cancelling',
    'complaints.cancel.hint': 'Only for lines registered by mistake or twice. A cancelled line leaves all reporting. Unfounded complaints are not cancelled: give them the solution "Unfounded".',
    'complaints.cancelreason': 'Cancelled because',
    'complaints.line.cancelled': 'Line cancelled',
    'complaints.line.reopened': 'Line reopened',
    'complaints.photos': 'Production photos',
    'complaints.nophotos': 'No PhotoQA photos linked to this order number.',
    'complaints.link': 'Copy link for factory',
    'complaints.link.title': 'Copies a link to this dossier; factory users see it read-only, with these photos.',
    'complaints.link.copied': 'Link copied',
    'complaints.attachments': 'Attachments',
    'complaints.upload': 'Upload file',
    'complaints.uploading': 'Uploading...',
    'complaints.uploaded': 'Uploaded',
    'complaints.uploaded.many': 'files uploaded',
    'complaints.noattachments': 'No attachments.',
    'complaints.noattachments.edit': 'Add the service report (Oranje, POS) or consumer photos, or drop files here.',
    'complaints.orderuploads': 'Uploads on the PhotoQA order',
    'complaints.remove': 'Remove',
    'complaints.serviceorders': 'Open service orders (Blueware)',
    'complaints.serviceorders.hint': 'Read-only from Blueware.',
    'complaints.timeline': 'Timeline',
    'complaints.noevents': 'No events yet.',
    'complaints.event.opened': 'Opened',
    'complaints.event.saved_incomplete': 'Saved incomplete',
    'complaints.event.saved_complete': 'Saved complete',
    'complaints.event.verdict_saved': 'Verdict saved',
    'complaints.event.closed': 'Closed',
    'complaints.event.reopened': 'Reopened',
    'complaints.event.cancelled': 'Cancelled',
    'complaints.event.ref_changed': 'References changed',
    'complaints.event.attachment_added': 'Attachment added',
    'complaints.event.attachment_removed': 'Attachment removed',
    // Guided line editor
    'complaints.phase1': 'Phase 1 - Complaint',
    'complaints.phase2': 'Phase 2 - Verdict',
    'complaints.field.article_type': 'Article type',
    'complaints.field.element_type': 'Element type',
    'complaints.field.main_complaint': 'Main complaint',
    'complaints.field.detail': 'Complaint detail',
    'complaints.field.description': 'Short description',
    'complaints.field.solution': 'Solution',
    'complaints.field.causer': 'Causer',
    'complaints.field.cost_bearer': 'Cost carrier',
    'complaints.optional': 'optional',
    'complaints.editor.noelement': 'Not applicable for {article}',
    'complaints.editor.change': 'Change',
    'complaints.editor.keep': 'Keep current answer',
    'complaints.editor.next': 'Next',
    'complaints.editor.verdictlater': 'Add the verdict when the outcome is known, for example when the service report arrives.',
    'complaints.editor.carehint': 'Causer and cost carrier are shown to the customer. Fill them in with care.',
    'complaints.editor.suggested': 'Pre-filled from the causer (cost schema). Change it if this case is different.',
    'complaints.editor.suggested.short': 'from causer',
    'complaints.editor.save.complete': 'Save complaint',
    'complaints.editor.save.solved': 'Save and close as solved',
    'complaints.editor.save.incomplete': 'Save as incomplete',
    'complaints.editor.missing': 'Missing',
    'complaints.editor.becomes': 'Status after saving:',
    'complaints.editor.nochanges': 'No changes to save.',
    'complaints.saving': 'Saving...',
    'complaints.saved': 'Saved',
    'complaints.saved.incomplete_complaint': 'Saved as incomplete complaint',
    'complaints.saved.pending_verdict': 'Saved - pending verdict',
    'complaints.saved.incomplete_verdict': 'Saved as incomplete verdict',
    'complaints.saved.solved': 'Saved and closed as solved',
    // Reports
    'complaints.tab.factory': 'Factory report',
    'complaints.tab.signals': 'Signal list',
    'complaints.tab.settings': 'Signal settings',
    'complaints.groupby': 'Group by',
    'complaints.group.model': 'Model',
    'complaints.group.detail': 'Complaint detail',
    'complaints.group.month': 'Production month',
    'complaints.pdf': 'PDF',
    'complaints.csv': 'Excel (CSV)',
    'complaints.pdf.preparing': 'Preparing...',
    'complaints.pdf.ready': 'File ready',
    'complaints.pdf.failed': 'Export failed',
    'complaints.pdf.timeout': 'The export took too long. Try again, or choose a shorter period.',
    'complaints.openoverview': 'Open complaints',
    'complaints.pdf.browser': 'Opening in your browser',
    'complaints.pdf.unsupported': 'This device cannot save the file here',
    'complaints.report.axis': 'Period and months follow the production date (photo date). Cancelled lines are left out.',
    'complaints.report.empty': 'No complaint lines',
    'complaints.report.empty.msg': 'Nothing registered for this factory and period.',
    'complaints.report.pendingnote': 'Pending: the verdict is still open, so no causer is determined for these lines yet.',
    'complaints.col.total': 'Lines',
    'complaints.col.solved': 'Solved',
    'complaints.col.pending': 'Pending verdict',
    'complaints.col.unfounded': 'Unfounded',
    'complaints.col.all': 'Total',
    'complaints.signals.hint': 'Same model and complaint detail repeating within the signal window, on the production date axis. Input for the production meeting.',
    'complaints.signals.none': 'No signals',
    'complaints.signals.none.msg': 'No model and complaint detail combination is over the threshold right now.',
    'complaints.signals.count': '{count}x in {weeks} weeks',
    'complaints.settings.title': 'Signal list threshold',
    'complaints.settings.hint': 'A signal is raised when the same model and complaint detail occur at least this many times within this many weeks of production.',
    'complaints.settings.threshold': 'Minimum number of lines',
    'complaints.settings.weeks': 'Within weeks',
    'complaints.settings.preview': 'Signal at {n} or more lines within {w} weeks.',
    'complaints.settings.invalid': 'Use at least 2 lines and at least 1 week',
    'complaints.settings.saved': 'Signal settings saved',
    // Sync bar, sync window and photo size per factory
    'sync.now': 'Sync now',
    'sync.busy': '⟳ Syncing…',
    'sync.result.sent': '✓ {sent} sent · {left} left',
    'sync.result.nothing': '✓ Nothing left to send',
    'sync.result.error': '⚠ Nothing sent, {left} still waiting · no connection to the server, retrying automatically',
    'sync.result.failed': '⚠ Sync failed',
    'sync.window.waiting': 'Uploads start at {time} · {count} photos waiting',
    'sync.window.hint': 'Photos stay safely on this tablet until the sync window opens. Sync now sends them right away.',
    'summary.held': 'Saved — photos stay on this tablet and upload from {time}',
    'sites.upload.title': 'Upload settings',
    'sites.upload.window': 'Sync window',
    'sites.upload.window.help': 'Photos only upload between these times, in the local time of the factory. Off = always sync. Orders and sessions always sync right away, and Sync now always works.',
    'sites.upload.window.incomplete': 'Choose a start and an end time (not the same) to switch the sync window on.',
    'sites.upload.start': 'From',
    'sites.upload.end': 'Until',
    'sites.upload.timezone': 'Timezone',
    'sites.upload.photo.help': 'Tablets shrink each new photo to this size before it is queued. Photos already waiting are not changed.',
    'sites.upload.maxside': 'Longest side (px)',
    'sites.upload.quality': 'JPEG quality (0.4-0.95)',
    'sites.upload.saved': 'Upload settings saved',
    'sites.upload.summary.always': 'Sync: always',
    'sites.upload.summary.window': 'Sync:',
    // Complaints in the order screen and Search
    'oc.register': 'Register complaint',
    'oc.info': 'Order information',
    'oc.commissionname': 'Commission name',
    'oc.commission.edit': 'Edit commission',
    'oc.commission.saved': 'Commission saved',
    'oc.commission.hint': 'What you type here is kept: a later Blueware/Vantage import only fills empty fields.',
    'oc.source.import': 'imported',
    'oc.service': 'Service',
    'oc.close': 'Close',
    'oc.timeline.all': 'All lines',
    'oc.timeline.filter': 'Filter timeline by line',
    'oc.search.filter': 'Complaints: all',
    'oc.search.open': 'Open complaints',
    'oc.search.solved': 'All solved',
    'oc.search.any': 'With complaints',
    'complaints.event.service_saved': 'Service details saved',
    // QC approval (order screen)
    'qc.title': 'Quality control (QC)',
    'qc.button': 'Approve QC',
    'qc.hint.ready': 'All {count} photos are on the server. Press the button when this set has passed quality control.',
    'qc.blocked': 'Cannot approve yet. Photos missing on the server: {steps}. Wait until they are synced, then try again.',
    'qc.step.0': 'Production label',
    'qc.step.1': 'Full set overview',
    'qc.step.2': 'Front view',
    'qc.step.3': 'Left side',
    'qc.step.4': 'Back view',
    'qc.step.5': 'Right side',
    'qc.confirm.title': 'Approve QC?',
    'qc.confirm.message': 'You are approving order {order} ({product}). Forge is told that this set has passed QC. You cannot undo this.',
    'qc.confirm.yes': 'Yes, approve',
    'qc.confirm.no': 'No, go back',
    'qc.saving': 'Saving...',
    'qc.done': 'QC approved on {time} by {name}',
    'qc.done.noname': 'QC approved on {time}',
    'qc.error.already': 'This order was already approved. Reload the order to see the approval.',
    'qc.error.missing': 'Not approved. These photos are not on the server yet: {steps}.',
    'qc.error.forbidden': 'You are not allowed to approve QC. Ask a Supervisor.',
    'qc.error.network': 'Not approved. No connection to the server. Check the connection and try again.',
    'qc.error.generic': 'Not approved. Something went wrong. Try again.',
  },
  pl: {
    'login.subtitle': 'System dokumentacji produkcji',
    'login.username': 'Nazwa użytkownika',
    'login.username.ph': 'Wprowadź nazwę użytkownika',
    'login.password': 'Hasło',
    'login.password.ph': 'Wprowadź hasło',
    'login.signin': 'Zaloguj się',
    'login.signingin': 'Logowanie...',
    'login.forgot': 'Nie pamiętasz hasła?',
    'login.forgot.msg': 'Skontaktuj się z administratorem, aby zresetować hasło.',
    'login.enterboth': 'Wprowadź nazwę użytkownika i hasło',
    'login.install': 'Zainstaluj aplikację na tablet',
    'login.secure': 'Bezpieczne · Wymuszone HTTPS · Dostęp wg ról',
    'login.offline.hint': 'Brak połączenia z internetem. Możesz się zalogować, jeśli wcześniej logowałeś się na tym tablecie.',
    'login.offline.unknown': 'Brak połączenia z internetem, a to konto nigdy nie logowało się na tym tablecie. Połącz się raz z internetem, aby się zalogować.',
    'login.offline.wrong': 'Błędne hasło. Odczekaj {seconds} s przed kolejną próbą.',
    'login.offline.wait': 'Zbyt wiele błędnych prób. Spróbuj ponownie za {seconds} s.',
    'login.offline.unsupported': 'Brak połączenia z internetem. Logowanie bez internetu nie jest dostępne na tym urządzeniu.',
    'login.offline.expired': 'Minęło ponad 14 dni od ostatniego logowania na tym tablecie z połączeniem internetowym. Połącz się z wifi i zaloguj się raz.',
    'login.session.rejected': 'Serwer zakończył sesję (zmiana hasła lub dezaktywacja konta). Zaloguj się ponownie z połączeniem internetowym. Zdjęcia i zamówienia w kolejce zostają zachowane.',
    'pw.set.title': 'Ustaw hasło',
    'pw.change.title': 'Zmień hasło',
    'pw.forced.note': 'Przed kontynuacją musisz ustawić nowe hasło. Tego kroku nie można pominąć.',
    'pw.current': 'Obecne hasło',
    'pw.current.ph': 'Wprowadź obecne hasło',
    'pw.new': 'Nowe hasło',
    'pw.new.ph': 'Wprowadź nowe hasło',
    'pw.confirm': 'Potwierdź nowe hasło',
    'pw.confirm.ph': 'Potwierdź nowe hasło',
    'pw.requirements': 'Wymagania hasła',
    'pw.req.length': 'Co najmniej 12 znaków',
    'pw.req.upper': 'Wielka litera',
    'pw.req.lower': 'Mała litera',
    'pw.req.number': 'Cyfra',
    'pw.req.special': 'Znak specjalny',
    'pw.show': 'Pokaż hasła',
    'pw.submit': 'Ustaw nowe hasło',
    'pw.updating': 'Aktualizowanie...',
    'pw.updated': 'Hasło zaktualizowane',
    'pw.nomatch': 'Hasła nie są zgodne',
    'pw.notmet': 'Hasło nie spełnia wymagań',
    'pw.current.required': 'Wymagane obecne hasło',
    'home.production': 'Produkcja',
    'home.newsession': 'Rozpocznij nową sesję',
    'home.myorders': 'Moje zlecenia',
    'home.newsession.short': 'Nowa sesja',
    'home.startphotographing': 'Rozpocznij fotografowanie',
    'home.searchorders': 'Szukaj zleceń',
    'home.findbyorder': 'Znajdź po numerze zlecenia',
    'home.reviewqueue': 'Kolejka recenzji',
    'home.reviewqueue.desc': 'Do omówienia, uwagi, komentarze',
    'nav.review': 'Recenzja',
    'home.feedback.title': 'Informacja zwrotna',
    'home.feedback.toreshoot': 'zdjęć do ponownego wykonania',
    'nav.home': 'Główna',
    'nav.new': 'Nowe',
    'nav.myorders': 'Zlecenia',
    'nav.feedback': 'Uwagi',
    'nav.search': 'Szukaj',
    'nav.settings': 'Ustawienia',
    'order.new': 'Nowe zlecenie',
    'order.model.search': 'Wpisz pierwsze litery modelu…',
    'order.model.nomatch': 'Nie znaleziono modelu. Sprawdź pisownię lub zaznacz poniżej „Undocumented model”, aby wpisać nazwę.',
    'order.model.selected': 'Model wybrany. Zmień tekst, aby wyszukać ponownie.',
    'order.step1': 'Krok 1 — Numer zlecenia',
    'order.scan': 'Zeskanuj lub wpisz numer zlecenia',
    'order.novalidation': 'Dowolny format — bez walidacji',
    'order.confirmnumber': 'Potwierdź numer zlecenia:',
    'order.confirm': 'Potwierdź numer zlecenia',
    'order.edit': 'Edytuj',
    'order.confirmed': 'Potwierdzono',
    'order.step2': 'Krok 2 — Nazwa produktu',
    'order.productph': 'np. Sofa model A',
    'order.autocomplete': 'Autouzupełnianie z poprzednich wpisów',
    'order.start': 'Rozpocznij sesję zdjęciową',
    'order.starting': 'Rozpoczynanie sesji...',
    'order.productrequired': 'Nazwa produktu jest wymagana',
    'order.offline.saved': 'Offline — sesja zapisana, zsynchronizuje się po połączeniu',
    'guided.step': 'Krok',
    'guided.of': 'z',
    'guided.captured': 'zrobione',
    'guided.restart': 'Zacznij od nowa',
    'guided.extra': 'Dodatkowe',
    'guided.taptotake': 'Dotknij, aby zrobić zdjęcie',
    'guided.orientation': 'Pionowo lub poziomo',
    'guided.retake': 'Zrób ponownie',
    'guided.na': 'Nd.',
    'guided.namarked': 'Oznaczono jako Nd.',
    'guided.tapinstead': 'Dotknij, aby zrobić zdjęcie',
    'guided.prev': 'Wstecz',
    'guided.next': 'Dalej',
    'guided.capture': 'Zrób zdjęcie',
    'guided.review': 'Przejrzyj',
    'guided.restart.title': 'Rozpocząć sesję od nowa?',
    'guided.restart.msg': 'Wszystkie zdjęcia w tej sesji zostaną odrzucone i zaczniesz od kroku 1.',
    'guided.restart.yes': 'Tak, zacznij od nowa',
    'guided.restarted': 'Sesja rozpoczęta od nowa',
    'summary.title': 'Przejrzyj sesję',
    'summary.photos': 'Zdjęcia',
    'summary.na': 'Nd.',
    'summary.missing': 'Brakujące',
    'summary.captured': 'Zrobione',
    'summary.notcaptured': 'Nie zrobiono',
    'summary.namarked': 'Oznaczono Nd.',
    'summary.take': 'Zrób',
    'summary.retake': 'Ponów',
    'summary.back': 'Wstecz',
    'summary.submit': 'Zatwierdź i zapisz',
    'summary.saving': 'Zapisywanie',
    'summary.offline.saved': 'Zapisano offline — zdjęcia zsynchronizują się po połączeniu',
    'feedback.title': 'Informacja zwrotna',
    'feedback.none': 'Brak zdjęć do ponownego wykonania',
    'feedback.toreshoot': 'do ponownego wykonania',
    'feedback.allclear': 'Wszystko w porządku',
    'feedback.allclear.msg': 'Żadne z Twoich zdjęć nie zostało odrzucone. Dobra robota!',
    'feedback.reshoot': 'Zrób ponownie',
    'feedback.rejected': 'Odrzucone',
    'feedback.nonote': 'brak uwagi',
    'feedback.done': 'Gotowe',
    'feedback.markeddone': 'Oznaczono jako gotowe',
    'common.cancel': 'Anuluj',
    'common.save': 'Zapisz',
    'common.signout': 'Wyloguj',
    'common.settings': 'Ustawienia',
    'home.complaints': 'Raporty',
    'home.complaints.desc': 'Raporty reklamacji wg fabryki, otwarte reklamacje',
    'nav.complaints': 'Raporty',
    'complaints.openoverview': 'Otwarte reklamacje',
    'complaints.pdf.timeout': 'Eksport trwał zbyt długo. Spróbuj ponownie lub wybierz krótszy okres.',
    'sync.now': 'Synchronizuj teraz',
    'sync.busy': '⟳ Synchronizacja…',
    'sync.result.sent': '✓ Wysłano: {sent} · pozostało: {left}',
    'sync.result.nothing': '✓ Nie ma nic do wysłania',
    'sync.result.error': '⚠ Nic nie wysłano, czeka: {left} · brak połączenia z serwerem, ponawiamy automatycznie',
    'sync.result.failed': '⚠ Synchronizacja nie powiodła się',
    'sync.window.waiting': 'Wysyłanie rozpocznie się o {time} · oczekujące zdjęcia: {count}',
    'sync.window.hint': 'Zdjęcia są bezpiecznie zapisane na tablecie do otwarcia okna synchronizacji. „Synchronizuj teraz” wysyła je od razu.',
    'summary.held': 'Zapisano — zdjęcia pozostają na tablecie i zostaną wysłane od {time}',
    'sites.upload.title': 'Ustawienia wysyłania',
    'sites.upload.window': 'Okno synchronizacji',
    'sites.upload.window.help': 'Zdjęcia są wysyłane tylko w tych godzinach, według czasu lokalnego fabryki. Wyłączone = synchronizacja zawsze. Zamówienia i sesje są wysyłane od razu, a „Synchronizuj teraz” działa zawsze.',
    'sites.upload.window.incomplete': 'Wybierz godzinę początku i końca (różne), aby włączyć okno synchronizacji.',
    'sites.upload.start': 'Od',
    'sites.upload.end': 'Do',
    'sites.upload.timezone': 'Strefa czasowa',
    'sites.upload.photo.help': 'Tablety zmniejszają każde nowe zdjęcie do tego rozmiaru przed dodaniem do kolejki. Zdjęcia już oczekujące nie są zmieniane.',
    'sites.upload.maxside': 'Dłuższy bok (px)',
    'sites.upload.quality': 'Jakość JPEG (0,4-0,95)',
    'sites.upload.saved': 'Zapisano ustawienia wysyłania',
    'sites.upload.summary.always': 'Synchronizacja: zawsze',
    'sites.upload.summary.window': 'Synchronizacja:',
    // Complaints in the order screen and Search
    'oc.register': 'Zarejestruj reklamację',
    'oc.info': 'Informacje o zamówieniu',
    'oc.commissionname': 'Nazwa komisji',
    'oc.commission.edit': 'Edytuj komisję',
    'oc.commission.saved': 'Komisja zapisana',
    'oc.commission.hint': 'To, co wpiszesz, zostaje zachowane: późniejszy import z Blueware/Vantage uzupełnia tylko puste pola.',
    'oc.source.import': 'zaimportowano',
    'oc.service': 'Serwis',
    'oc.close': 'Zamknij',
    'oc.timeline.all': 'Wszystkie pozycje',
    'oc.timeline.filter': 'Filtruj oś czasu według pozycji',
    'oc.search.filter': 'Reklamacje: wszystkie',
    'oc.search.open': 'Otwarte reklamacje',
    'oc.search.solved': 'Wszystkie rozwiązane',
    'oc.search.any': 'Z reklamacjami',
    'complaints.event.service_saved': 'Dane serwisowe zapisane',
    'complaints.line': 'Pozycja',
    'complaints.lines': 'Pozycje reklamacji',
    'complaints.addline': 'Dodaj pozycję reklamacji',
    'complaints.summary': '{solved} z {total} rozwiązanych',
    'complaints.status.incomplete_complaint': 'Niepełna reklamacja',
    'complaints.status.pending_verdict': 'Oczekuje na decyzję',
    'complaints.status.incomplete_verdict': 'Niepełna decyzja',
    'complaints.status.solved': 'Rozwiązana',
    'complaints.status.cancelled': 'Anulowana',
    'complaints.timeline': 'Oś czasu',
    'complaints.noevents': 'Brak zdarzeń.',
    'complaints.event.opened': 'Otwarta',
    'complaints.event.saved_incomplete': 'Zapisana jako niepełna',
    'complaints.event.saved_complete': 'Zapisana jako pełna',
    'complaints.event.verdict_saved': 'Decyzja zapisana',
    'complaints.event.closed': 'Zamknięta',
    'complaints.event.reopened': 'Otwarta ponownie',
    'complaints.event.cancelled': 'Anulowana',
    'complaints.event.uncancelled': 'Anulowanie cofnięte',
    'complaints.customer': 'Klient',
    'complaints.model': 'Model',
    'complaints.productiondate': 'Data produkcji',
    'complaints.deliverydate': 'Data dostawy',
    'complaints.commission': 'Nr komisji',
    'complaints.partner': 'Partner serwisowy',
    'complaints.caseref': 'Numer sprawy serwisowej',
    'complaints.continue': 'Kontynuuj',
    'complaints.reopen': 'Otwórz ponownie',
    'complaints.uncancel': 'Cofnij anulowanie',
    'complaints.cancelline': 'Anuluj pozycję',
    'complaints.nodetail': 'Reklamacja jeszcze nieokreślona',
    'complaints.nolines': 'Brak pozycji reklamacji. Każdy problem dostaje własną pozycję i status.',
    'complaints.field.solution': 'Rozwiązanie',
    'complaints.field.causer': 'Przyczyna',
    'complaints.field.cost_bearer': 'Koszty ponosi',
    'complaints.cancel.reason': 'Powód anulowania',
    'complaints.cancelreason': 'Anulowano, ponieważ',
    'complaints.line.cancelled': 'Pozycja anulowana',
    'complaints.line.reopened': 'Pozycja otwarta ponownie',
    'complaints.line.uncancelled': 'Anulowanie cofnięte',
    'complaints.saved': 'Zapisano',
    'complaints.saving': 'Zapisywanie...',
    // QC approval (order screen)
    'qc.title': 'Kontrola jakości (QC)',
    'qc.button': 'Zatwierdź QC',
    'qc.hint.ready': 'Wszystkie zdjęcia ({count}) są na serwerze. Naciśnij przycisk, gdy ten zestaw przeszedł kontrolę jakości.',
    'qc.blocked': 'Nie można jeszcze zatwierdzić. Brakuje na serwerze zdjęć: {steps}. Poczekaj na synchronizację i spróbuj ponownie.',
    'qc.step.0': 'Etykieta produkcyjna',
    'qc.step.1': 'Widok całego zestawu',
    'qc.step.2': 'Widok z przodu',
    'qc.step.3': 'Lewa strona',
    'qc.step.4': 'Widok z tyłu',
    'qc.step.5': 'Prawa strona',
    'qc.confirm.title': 'Zatwierdzić QC?',
    'qc.confirm.message': 'Zatwierdzasz zamówienie {order} ({product}). Forge otrzyma informację, że ten zestaw przeszedł kontrolę jakości. Tego nie można cofnąć.',
    'qc.confirm.yes': 'Tak, zatwierdzam',
    'qc.confirm.no': 'Nie, wróć',
    'qc.saving': 'Zapisywanie...',
    'qc.done': 'QC zatwierdzone {time} przez {name}',
    'qc.done.noname': 'QC zatwierdzone {time}',
    'qc.error.already': 'To zamówienie zostało już zatwierdzone. Odśwież zamówienie, aby zobaczyć zatwierdzenie.',
    'qc.error.missing': 'Nie zatwierdzono. Tych zdjęć nie ma jeszcze na serwerze: {steps}.',
    'qc.error.forbidden': 'Nie masz uprawnień do zatwierdzania QC. Poproś Supervisora.',
    'qc.error.network': 'Nie zatwierdzono. Brak połączenia z serwerem. Sprawdź połączenie i spróbuj ponownie.',
    'qc.error.generic': 'Nie zatwierdzono. Coś poszło nie tak. Spróbuj ponownie.',
  },
};
